import { SliderStep } from './SliderStep';
import { Localizer } from '../../locale/useTextLocalizer';

export type SliderFormatParseResult =
  | { type: 'success'; result: number }
  | { type: 'error'; result: string };

export interface SliderFormat {
  /**
   * Formats text that will be:
   * - displayed in a tick mark or grip label
   * - read to screen readers
   *
   * @param value the value to format.
   */
  format(value: number): string;
  /**
   * Formats text that users will edit in grip inputs.
   *
   * If not specified, a default, locale-aware formatter will be used.
   *
   * @param value the value to format.
   * @see SliderDefaultFormat
   */
  formatEditText?(value: number): string;
  /**
   * Parses text from a user entered string.
   */
  parse(inputText: string, localize: Localizer, step?: SliderStep): SliderFormatParseResult;
}

/**
 * @internal
 */
export class SliderDefaultFormat implements SliderFormat {
  private readonly _numberFormat: Intl.NumberFormat;
  private readonly _disableRoundToStep: boolean | undefined;

  private readonly _patternDecimal: RegExp;
  private readonly _patternThousands: RegExp;
  private readonly _patternCurrency: RegExp;
  private readonly validRegEx: RegExp;

  constructor(
    formatLocale?: string,
    formatOptions?: Intl.NumberFormatOptions,
    disableRoundToStep?: boolean
  ) {
    this._numberFormat = new Intl.NumberFormat(formatLocale, formatOptions);
    this._disableRoundToStep = disableRoundToStep;

    // It seems that formatToParts is a better way to get the
    // delimiters for different locales than using toLocaleString()
    const parts = this._numberFormat.formatToParts(11000.5);
    let currency = '',
      thousands = '',
      decimal = '';
    parts.forEach((element) => {
      switch (element.type) {
        case 'currency':
          currency = element.value;
          break;
        case 'group':
          thousands = element.value;
          break;
        case 'decimal':
          decimal = element.value;
          break;
      }
    });
    // if the thousands separator is a narrow non-breaking space
    // or non-breaking space, allow the user to enter a space
    const adjustedThousands =
      thousands.charCodeAt(0) === 8239 || thousands.charCodeAt(0) === 160
        ? ` |${thousands}`
        : thousands;

    this._patternDecimal = new RegExp(`[${decimal}]`, 'gu');
    this._patternThousands = new RegExp(`[${adjustedThousands}]`, 'gu');
    this._patternCurrency = new RegExp(`[${currency}]`, 'gu');

    // a valid number is made up of the following:
    // whitespace before the number
    // optional negative sign
    // 1-3 digits
    // any number of thousands separator followed by 3 digits, or just any number of digits
    // optional decimal separator followed by any number of digits
    // whitespace after the number
    this.validRegEx = new RegExp(
      `^\\s*-?(\\d{1,3}([${adjustedThousands}](\\d{3})){0,}|\\d{0,})([${decimal}](\\d){0,})?\\s*$`,
      'gu'
    );
  }

  format(value: number): string {
    // avoid an error when user presses escape key
    return this ? this._numberFormat.format(value) : '';
  }

  parse(inputText: string, localize: Localizer, step?: SliderStep): SliderFormatParseResult {
    // convert all hindu-arabic and persian numbers to english
    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g],
      arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    for (let i = 0; i < 10; i++) {
      inputText = inputText
        .replace(persianNumbers[i], i.toString())
        .replace(arabicNumbers[i], i.toString());
    }
    // if the string contains an RTL mark, remove it before validation
    if (inputText.charCodeAt(0) === 8206 || inputText.charCodeAt(0) === 1564)
      inputText = inputText.substring(1);

    // remove any currency symbol before validation
    inputText = inputText.replace(this._patternCurrency, '');

    // make sure the number is in a valid format
    this.validRegEx.lastIndex = 0;
    if (this.validRegEx.test(inputText) === false) {
      return { type: 'error', result: localize('input.invalid.error.msg') };
    }
    inputText = inputText
      .trim()
      // remove the thousands separator
      .replace(this._patternThousands, '')
      // replace the decimal pattern with a period
      .replace(this._patternDecimal, '.');

    if (inputText === '') {
      return { type: 'error', result: localize('input.invalid.error.msg') };
    }

    let result = Number(inputText);

    if (isNaN(result)) {
      return { type: 'error', result: localize('input.invalid.error.msg') };
    } else {
      result = calculatePreciseValue(result, step, this._numberFormat, this._disableRoundToStep);
      return { type: 'success', result };
    }
  }
}

export const calculatePreciseValue = (
  value: number,
  step?: SliderStep,
  numberFormat?: Intl.NumberFormat,
  disableRoundToStep?: boolean
) => {
  // if disableRoundToStep is true, then we do not
  // round the values to align with the step size
  // so just return - values can be any precision
  if (typeof step !== 'number' || disableRoundToStep) return value;
  let precision = 0;
  let stepMultiplier = step as number;
  if (step === 1) {
    return Math.round(value);
  } else {
    if (step > 1) {
      const numFormat = numberFormat ?? new Intl.NumberFormat();
      const locale = numFormat.resolvedOptions().locale;
      const decimal = (1.1).toLocaleString(locale).replace(/1/gu, '');
      const localizedStep = step.toLocaleString(locale);
      precision = localizedStep.split(decimal)[1]?.length ?? 0;
    } else {
      // if the step is less than 1, calculate the new value
      // as a multiple of that step
      while (stepMultiplier !== 0 && stepMultiplier < 1) {
        precision += 1;
        stepMultiplier *= 10;
      }
    }
    // the following logic came from the openUi5 slider
    let divValue = value / step;
    divValue = parseFloat(divValue.toFixed(precision));
    const divFloor = Math.floor(divValue);
    // can't use % operator because it has double precision problems
    // for example 1 % .01 is not 0
    const modVal = (divValue - divFloor) * step;
    const numForRound = step / 2;
    let adjustedValue = divFloor * step;

    if (modVal >= numForRound) {
      adjustedValue = adjustedValue + step;
    }

    return parseFloat(adjustedValue.toFixed(precision));
  }
};
